import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import PublicPageBlocksView from 'src/common/components/PublicPageBlocksView';
import HomeHeroSectionLayout from 'src/common/components/home-hero/HomeHeroSectionLayout';
import { frostedPanelSx } from 'src/common/styles/frostedPanelStyles';
import { createDefaultHomeDocument, getHeroBlocks } from 'src/helpers/pageDocumentUtils';
import { LanguageKeys } from 'src/common/constants/languages';
import { IHeroSettings } from 'src/interfaces/HeroSettings';
import { IPageBlock, PageBlockType } from 'src/interfaces/PageBlock';
import { ITrackMap } from 'src/interfaces/TrackMap';
import HeroBackgroundPanel from 'src/pages/Dashboard/System/Pages/components/HeroBackgroundPanel';
import PageDocumentEditor from 'src/pages/Dashboard/System/Pages/components/PageDocumentEditor';
import PageBlocksSectionHeader from 'src/pages/Dashboard/System/Pages/components/PageBlocksSectionHeader';
import { LanguageFlagButtons } from 'src/pages/Dashboard/System/Pages/components/LanguageFlagSwitcher';
import type { IPageDocument } from 'src/helpers/pageDocumentUtils';

const formPaperSx = {
  ...frostedPanelSx(0.5),
  p: { xs: 2, sm: 3 },
  backdropFilter: 'none',
  mt: 2,
};

interface Props {
  document: IPageDocument;
  onDocumentChange: (doc: IPageDocument) => void;
  selectedEditorLang: LanguageKeys;
  setSelectedEditorLang: (lang: LanguageKeys) => void;
  contentKey: string;
  pageContentId?: string;
  trackMaps: ITrackMap[];
  onMoveBlock: (id: string, direction: 'up' | 'down') => void;
  onMoveByDrag: (activeId: string, overId: string) => void;
  onUpdate: (id: string, patch: Partial<IPageBlock>) => void;
  onRemove: (id: string) => void;
  onAddAt: (
    zone: 'hero' | 'body',
    type: PageBlockType,
    columnCount?: 2 | 3 | 4,
    insertIndex?: number,
  ) => void;
}

export default function HomeHeroSectionEditor({
  document,
  onDocumentChange,
  selectedEditorLang,
  setSelectedEditorLang,
  contentKey,
  pageContentId,
  trackMaps,
  onMoveBlock,
  onMoveByDrag,
  onUpdate,
  onRemove,
  onAddAt,
}: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobilePreview = useMediaQuery(theme.breakpoints.down('md'));

  const heroSettings = document.heroSettings;
  const previewBlocks = useMemo(() => {
    const blocks = getHeroBlocks(document);
    return blocks.length ? blocks : getHeroBlocks(createDefaultHomeDocument());
  }, [document]);

  const handleHeroSettingsChange = (settings: IHeroSettings) => {
    onDocumentChange({ ...document, heroSettings: settings });
  };

  return (
    <Paper elevation={6} sx={formPaperSx}>
      <PageBlocksSectionHeader
        title={t('system.pages.hero.sectionTitle')}
        actions={
          <LanguageFlagButtons
            value={selectedEditorLang}
            onChange={setSelectedEditorLang}
          />
        }
      />

      <HeroBackgroundPanel
        heroSettings={heroSettings}
        onChange={handleHeroSettingsChange}
        contentKey={contentKey}
        pageContentId={pageContentId}
      />

      <PageDocumentEditor
        document={document}
        onDocumentChange={onDocumentChange}
        contentKey={contentKey}
        pageContentId={pageContentId}
        selectedEditorLang={selectedEditorLang}
        setSelectedEditorLang={setSelectedEditorLang}
        trackMaps={trackMaps}
        showHeroZone
        showBodyZone={false}
        onMoveBlock={onMoveBlock}
        onMoveByDrag={onMoveByDrag}
        onUpdate={onUpdate}
        onRemove={onRemove}
        onAddAt={onAddAt}
      />

      <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
        {t('system.pages.hero.preview')}{' '}
        <Typography component="span" variant="caption" color="text.secondary">
          ({isMobilePreview ? t('system.pages.hero.previewMobile') : t('system.pages.hero.previewDesktop')})
        </Typography>
      </Typography>
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <HomeHeroSectionLayout
          contained
          backgroundImageUrl={heroSettings?.backgroundImageUrl}
          backgroundPosition={`${heroSettings?.backgroundAlignH ?? 'center'} ${heroSettings?.backgroundAlignV ?? 'center'}`}
          aria-label={t('system.pages.hero.preview')}
        >
          <PublicPageBlocksView
            blocks={previewBlocks}
            lang={selectedEditorLang}
            contentKey={contentKey}
            inHero
          />
        </HomeHeroSectionLayout>
      </Paper>
    </Paper>
  );
}
